import { toLocalDay } from '../days';
import type { ExperienceState } from '../state/experienceState';

import {
  type CampaignDecision,
  decideAfterEligibility,
  holdoutBucket,
  precheckCampaign,
} from './campaignPolicy';
import type { Campaign, CampaignContext } from './campaignTypes';

const ELIGIBILITY_TTL_MS = 5 * 60 * 1000;

const eligibility = new Map<
  string,
  { checkedAt: number; result: Promise<boolean> }
>();

const checkEligibility = (
  id: string,
  isEligible: NonNullable<Campaign['isEligible']>,
  context: CampaignContext,
): Promise<boolean> => {
  const key = `${id}:${context.userId}`;
  const known = eligibility.get(key);
  if (known != null && Date.now() - known.checkedAt < ELIGIBILITY_TTL_MS) {
    return known.result;
  }
  const result = isEligible(context).catch(eligibilityError => {
    console.warn(`Campaign ${id}: data check failed`, eligibilityError);
    if (eligibility.get(key)?.result === result) {
      eligibility.delete(key);
    }
    return false;
  });
  eligibility.set(key, { checkedAt: Date.now(), result });
  return result;
};

// The full decision for one campaign and the current user: cheap checks
// first, then the campaign's own data check, then the holdout split. Used by
// the tour engine to start tours and by the admin view to explain why not.
export const evaluateCampaign = async ({
  id,
  campaign,
  context,
  state,
  autoTourShownThisSession,
  isImpersonating,
}: {
  id: string;
  campaign: Campaign;
  context: CampaignContext;
  state: ExperienceState;
  autoTourShownThisSession: boolean;
  isImpersonating: boolean;
}): Promise<CampaignDecision> => {
  const now = new Date();
  const precheck = precheckCampaign({
    id,
    campaign,
    state,
    viewer: context.viewer,
    now,
    today: toLocalDay(now),
    autoTourShownThisSession,
    isImpersonating,
  });
  if (precheck != null) {
    return precheck;
  }
  const eligible =
    campaign.isEligible == null
      ? true
      : await checkEligibility(id, campaign.isEligible, context);
  return decideAfterEligibility({
    eligible,
    holdout: campaign.holdout,
    bucket: holdoutBucket(context.userId, id),
  });
};
